const DROPBOX_EMOJI_IMAGE_FRAGMENT = 'paper.dropboxstatic.com/static/img/ace/emoji';
const DEFAULT_OBJECT_STRING = '[object Object]';

type SerializableCommentEditorState = {
  toJSON: () => unknown;
  toString: () => string;
};

type SerializedCommentNode = {
  children?: SerializedCommentNode[];
  src?: unknown;
  text?: unknown;
  type?: unknown;
  [key: string]: unknown;
};

export const isAllowedCommentImageSrc = (src: unknown): src is string =>
  typeof src === 'string' && src.includes(DROPBOX_EMOJI_IMAGE_FRAGMENT);

export const serializeCommentEditorState = (
  editorState: SerializableCommentEditorState | undefined | null,
  originalContent?: string
): string => {
  // No editor state means there is no draft content to save.
  if (!editorState) {
    return '';
  }

  const stringValue = editorState.toString().trim();
  // RichTextArea intentionally returns an empty string for visually empty comments.
  if (!stringValue) {
    return '';
  }

  // RichTextArea overrides toString() with serialized Lexical JSON once the user edits content.
  if (stringValue !== DEFAULT_OBJECT_STRING) {
    return stringValue;
  }

  // Parsed-but-unchanged Lexical EditorState can still use Object.toString(), which is invalid JSON.
  // Preserve the original serialized comment instead of saving "[object Object]".
  const fallbackContent = originalContent?.trim();
  if (fallbackContent) {
    return fallbackContent;
  }

  // If no original content is available, serialize through Lexical's real JSON API.
  return JSON.stringify(editorState.toJSON()) ?? '';
};

const isRegularImageNode = (node: SerializedCommentNode): boolean =>
  node.type === 'image' && !isAllowedCommentImageSrc(node.src);

const isMeaningfulNode = (node: SerializedCommentNode | null): boolean => {
  if (!node) {
    return false;
  }

  if (isRegularImageNode(node) || node.type === 'linebreak') {
    return false;
  }

  if (node.type === 'image') {
    return isAllowedCommentImageSrc(node.src);
  }

  if (node.type === 'text') {
    return typeof node.text === 'string' && node.text.trim().length > 0;
  }

  if (Array.isArray(node.children)) {
    return node.children.some(isMeaningfulNode);
  }

  return true;
};

const trimEmptyNodes = (nodes: SerializedCommentNode[]): SerializedCommentNode[] => {
  const firstMeaningful = nodes.findIndex(isMeaningfulNode);
  if (firstMeaningful === -1) {
    return [];
  }

  let lastMeaningful = nodes.length - 1;
  while (lastMeaningful >= firstMeaningful && !isMeaningfulNode(nodes[lastMeaningful])) {
    lastMeaningful--;
  }

  return nodes.slice(firstMeaningful, lastMeaningful + 1);
};

const trimTextInNode = (
  node: SerializedCommentNode,
  trimStart: boolean,
  trimEnd: boolean
): SerializedCommentNode => {
  const nextNode = {...node};

  if (node.type === 'text' && typeof node.text === 'string') {
    let text = node.text;
    if (trimStart) text = text.trimStart();
    if (trimEnd) text = text.trimEnd();
    nextNode.text = text;
  }

  if (Array.isArray(node.children) && node.children.length > 0) {
    nextNode.children = node.children.map((child, index) => {
      const isFirst = index === 0;
      const isLast = index === node.children!.length - 1;
      return trimTextInNode(child, isFirst && trimStart, isLast && trimEnd);
    });
  }

  return nextNode;
};

const stripImagesFromNode = (node: SerializedCommentNode): SerializedCommentNode | null => {
  if (isRegularImageNode(node)) {
    return null;
  }

  const nextNode = {...node};

  if (Array.isArray(node.children)) {
    nextNode.children = trimEmptyNodes(
      node.children
        .map(stripImagesFromNode)
        .filter((child): child is SerializedCommentNode => child !== null)
    );
  }

  return nextNode;
};

const isSerializedCommentNode = (value: unknown): value is SerializedCommentNode =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const sanitizeSerializedLexicalComment = (content: string): string | null => {
  const trimmedContent = content.trim();
  if (!trimmedContent.startsWith('{') && !trimmedContent.startsWith('[')) {
    return null;
  }

  try {
    const parsed = JSON.parse(content);

    if (!isSerializedCommentNode(parsed)) {
      return content;
    }

    if (isSerializedCommentNode(parsed.root)) {
      const sanitizedRoot = stripImagesFromNode(parsed.root);
      if (!sanitizedRoot || !isMeaningfulNode(sanitizedRoot)) {
        return '';
      }

      const trimmedRoot = trimTextInNode(sanitizedRoot, true, true);
      return JSON.stringify({...parsed, root: trimmedRoot});
    }

    const sanitized = stripImagesFromNode(parsed);

    if (!sanitized || !isMeaningfulNode(sanitized)) {
      return '';
    }

    const trimmed = trimTextInNode(sanitized, true, true);
    return JSON.stringify(trimmed);
  } catch {
    return null;
  }
};

const stripMarkdownImages = (content: string): string =>
  content.replace(/!\[[^\]]*]\([^)]*\)/g, '').trim();

const stripHtmlImages = (content: string): string => {
  if (!content.toLowerCase().includes('<img')) {
    return content;
  }

  if (typeof DOMParser === 'undefined') {
    return content.replace(/<img\b[^>]*>/gi, '').trim();
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(content, 'text/html');
  document.querySelectorAll('img').forEach((image) => {
    if (!isAllowedCommentImageSrc(image.getAttribute('src') ?? image.src)) {
      image.remove();
    }
  });

  return document.body.innerHTML.trim();
};

const hasMeaningfulFallbackContent = (content: string): boolean => {
  if (!content.trim()) {
    return false;
  }

  if (typeof DOMParser === 'undefined' || !content.includes('<')) {
    return content.trim().length > 0;
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(content, 'text/html');
  const hasText = Boolean(document.body.textContent?.trim());
  const hasAllowedImage = Array.from(document.querySelectorAll('img')).some((image) =>
    isAllowedCommentImageSrc(image.getAttribute('src') ?? image.src)
  );

  return hasText || hasAllowedImage;
};

export const sanitizeCommentContent = (content: string | undefined | null): string => {
  if (!content?.trim()) {
    return '';
  }

  const serializedComment = sanitizeSerializedLexicalComment(content);
  if (serializedComment !== null) {
    return serializedComment;
  }

  const contentWithoutImages = stripMarkdownImages(stripHtmlImages(content));
  return hasMeaningfulFallbackContent(contentWithoutImages) ? contentWithoutImages : '';
};
